/**
 * Shared helper for turning axios/fetch error responses into human-readable
 * strings suitable for toast.error() and inline form errors.
 *
 * The Serviti API returns errors in TWO shapes:
 *
 *   1. FastAPI HTTPException — `{ "detail": "Plain string message" }`
 *      Routes that raise `HTTPException(status_code=400, detail="...")` use
 *      this. `detail` is always a string.
 *
 *   2. Pydantic 422 validation error — `{ "detail": [ {loc, msg, type}, ... ] }`
 *      Fired when request body fails Pydantic schema validation (missing
 *      required field, empty string where validator forbids it, wrong type).
 *      `detail` is an ARRAY of objects, NOT a string.
 *
 * Until this helper existed, every catch block did `toast.error(error.response
 * .data.detail || 'Failed')` — which silently broke on Pydantic 422s because
 * passing an array of objects to toast.error() blanks the React tree.
 * Result: user submits an empty form, sees a blank page instead of a clear
 * "name: cannot be blank" message.
 *
 * Pass the axios error AND a fallback string. The helper returns a string
 * that's always safe to render.
 */
export function getApiErrorMessage(error, fallback = 'Operation failed') {
  // Defensive: error may be null, undefined, a plain string, or non-axios
  if (!error) return fallback;

  // Network error (no response at all) — connection refused, DNS, CORS preflight, etc.
  if (!error.response) {
    if (error.message && typeof error.message === 'string') return error.message;
    return 'Network error — please check your connection and try again';
  }

  const detail = error.response?.data?.detail;

  // Shape 1: plain string detail (FastAPI HTTPException)
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  // Shape 2: Pydantic 422 — array of {loc, msg, type, ...}
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => {
        // loc is an array like ["body", "name"] — take the last segment as the field name
        const fieldName =
          d?.loc && Array.isArray(d.loc) && d.loc.length > 0
            ? String(d.loc[d.loc.length - 1])
            : 'Field';
        // Pydantic prefixes validator-raised errors with "Value error, " — strip it
        // so the user sees the actual message we wrote in the validator.
        const cleanMsg = (d?.msg || '').replace(/^Value error,\s*/i, '');
        return cleanMsg ? `${fieldName}: ${cleanMsg}` : fieldName;
      })
      .filter(Boolean)
      .join('; ');
  }

  // Shape 3: server returned 5xx with no parseable detail
  if (error.response?.status >= 500) {
    return `Server error (${error.response.status}) — please try again or contact support`;
  }

  // Shape 4: 4xx with a status but no detail (rare — usually means the backend
  // raised an unhandled exception that FastAPI wrapped without our error shape)
  if (error.response?.status) {
    return `${fallback} (HTTP ${error.response.status})`;
  }

  return fallback;
}
